import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useClients } from '../context/ClientsContext';
import { useApiKeys } from '../context/ApiKeysContext';
import './webhooks.css';

const PAGE_SIZE = 7;

const EMPTY_FORM = {
  name: '',
  key: '',
  status: 'active',
  company_id: '',
};

function resolveCompanyIdForFilter(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('administrator')) {
    return (
      user?.activeCompany?.id ??
      user?.activeCompany?.company_id ??
      user?.activeCompany?.companyId ??
      ''
    );
  }
  return (
    user?.myCompany?.id ??
    user?.myCompany?.company_id ??
    user?.myCompany?.companyId ??
    ''
  );
}

function resolveDefaultCompanyId(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('administrator')) {
    const activeId =
      user?.activeCompany?.id ??
      user?.activeCompany?.company_id ??
      user?.activeCompany?.companyId;
    if (activeId != null && String(activeId).trim() !== '') {
      return String(activeId);
    }
  }
  const myId =
    user?.myCompany?.id ??
    user?.myCompany?.company_id ??
    user?.myCompany?.companyId;
  return myId != null && String(myId).trim() !== '' ? String(myId) : '';
}

function formatCreated(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function generateApiKeyValue() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function copyTextToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy copy
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export default function ApiKeys() {
  const location = useLocation();
  const { currentTheme, theme } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { clients: companies, refreshClients } = useClients();
  const {
    apiKeys,
    total,
    refreshApiKeys,
    createApiKey,
    getApiKey,
    updateApiKey,
    deleteApiKey,
    loading,
    saving,
    error,
  } = useApiKeys();

  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState('edit');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showKey, setShowKey] = useState(false);
  const [originalKey, setOriginalKey] = useState('');
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [generatedKeyWarning, setGeneratedKeyWarning] = useState(false);

  const filterCompanyId = resolveCompanyIdForFilter(user);

  const companyOptions = useMemo(() => {
    return (Array.isArray(companies) ? companies : [])
      .filter((c) => c?.id != null && c?.id !== '')
      .map((c) => ({
        id: String(c.id),
        name: c?.name || String(c.id),
      }));
  }, [companies]);

  const companyById = useMemo(() => {
    const map = new Map();
    companyOptions.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [companyOptions]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    refreshApiKeys?.({
      limit: PAGE_SIZE,
      offset,
      companyId: filterCompanyId || undefined,
    });
  }, [refreshApiKeys, page, filterCompanyId]);

  const rows = useMemo(() => {
    return (Array.isArray(apiKeys) ? apiKeys : []).map((row, idx) => ({
      status: row?.status ?? '—',
      id: row?.id ?? `${idx}`,
      name: row?.name ?? '—',
      keyMasked: row?.hint ?? '***',
      companyId: row?.company_id ?? row?.companyId ?? '—',
      companyName: (() => {
        const cid = row?.company_id ?? row?.companyId;
        if (cid == null || cid === '') return '—';
        return companyById.get(String(cid)) || String(cid);
      })(),
      createdAt: row?.created_at ?? row?.createdAt ?? null,
      raw: row,
    }));
  }, [apiKeys, companyById]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const canSaveForm =
    String(form.name || '').trim() !== '' &&
    (editMode === 'edit' ||
      (String(form.key || '').trim() !== '' && String(form.company_id || '').trim() !== ''));

  const openCreate = () => {
    refreshClients?.();
    setEditMode('create');
    setEditing({ id: null });
    setShowKey(true);
    setOriginalKey('');
    setKeyLoading(false);
    setKeyCopied(false);
    setGeneratedKeyWarning(false);
    setForm({
      ...EMPTY_FORM,
      company_id: resolveDefaultCompanyId(user),
    });
  };
  useEffect(() => { if (location.state?.quickCreate) openCreate(); }, [location.state]);

  const openEdit = async (row) => {
    refreshClients?.();
    setEditMode('edit');
    setEditing(row);
    setShowKey(false);
    setOriginalKey('');
    setKeyCopied(false);
    setGeneratedKeyWarning(false);
    const cid = row.companyId === '—' ? '' : String(row.companyId);
    setForm({
      name: row.name === '—' ? '' : String(row.name),
      key: '',
      status: row.status === '—' ? 'active' : String(row.status),
      company_id: cid,
    });

    setKeyLoading(true);
    try {
      const res = await getApiKey(row.id);
      if (res?.ok) {
        const loadedKey = String(
          res.data?.hint ??
          res.data?.key_masked ??
          res.data?.key ??
          row?.keyMasked ??
          ''
        ).trim();
        setForm((f) => ({ ...f, key: loadedKey }));
        setOriginalKey(loadedKey);
      }
    } finally {
      setKeyLoading(false);
    }
  };

  const closeEdit = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowKey(false);
    setOriginalKey('');
    setKeyLoading(false);
    setKeyCopied(false);
    setGeneratedKeyWarning(false);
  };

  const onGenerateKey = () => {
    setForm((f) => ({ ...f, key: generateApiKeyValue() }));
    setShowKey(true);
    setKeyCopied(false);
    setGeneratedKeyWarning(true);
  };

  const onCopyKey = async () => {
    const value = String(form.key || '').trim();
    if (!value || keyLoading) return;
    const ok = await copyTextToClipboard(value);
    if (ok) {
      setKeyCopied(true);
      window.setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const reloadCurrentPage = async (targetPage = safePage) => {
    const offset = (targetPage - 1) * PAGE_SIZE;
    await refreshApiKeys({
      limit: PAGE_SIZE,
      offset,
      companyId: filterCompanyId || undefined,
    });
  };

  const onSave = async () => {
    const name = String(form.name || '').trim();
    if (!name) return;

    if (editMode === 'create') {
      const key = String(form.key || '').trim();
      const selectedCompanyId = String(form.company_id || '').trim();
      if (!key || !selectedCompanyId) return;

      const res = await createApiKey({
        name,
        key,
        status: form.status || 'active',
        company_id: selectedCompanyId,
      });
      if (res?.ok) {
        closeEdit();
        setPage(1);
        await reloadCurrentPage(1);
      }
      return;
    }

    const payload = { name, status: form.status || 'active' };
    const key = String(form.key || '').trim();
    if (key && key !== originalKey) {
      payload.key = key;
    }

    const res = await updateApiKey(editing.id, payload);
    if (res?.ok) {
      closeEdit();
      await reloadCurrentPage();
    }
  };

  const onDelete = async () => {
    if (!editing?.id) return;
    const res = await deleteApiKey(editing.id);
    if (res?.ok) {
      closeEdit();
      const nextPage = rows.length <= 1 && safePage > 1 ? safePage - 1 : safePage;
      setPage(nextPage);
      await reloadCurrentPage(nextPage);
    }
  };

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main className="webhooks-main">
        <h1 className="webhooks-title">{t('apiKeys')}</h1>

        {loading && (
          <div className="webhooks-muted">{t('loading')}</div>
        )}

        {!loading && error && (
          <div className={`webhooks-error ${theme}`}>{t(error)}</div>
        )}

        {!loading && (
          <div className="webhooks-tableWrap">
            <div className={`webhooks-tableTopbar ${theme}`}>
              <div className="webhooks-tableTopbarTitle">
                {t('apiKeysPanel')}: {total}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  onClick={openCreate}
                  disabled={saving}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    saving ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {t('create')}
                </button>
                <button
                  type="button"
                  onClick={() => reloadCurrentPage()}
                  disabled={loading || saving}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    loading || saving ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {loading ? `${t('loading')}...` : t('refresh')}
                </button>
              </div>
            </div>

            {rows.length > 0 ? (
              <table className="webhooks-table">
                <thead>
                  <tr>
                    <th className="webhooks-th">{t('apiKeyName')}</th>
                    <th className="webhooks-th">{t('apiKeyValue')}</th>
                    <th className="webhooks-th">{t('apiKeyCompany')}</th>
                    <th className="webhooks-th">{t('Status')}</th>
                    <th className="webhooks-th">{t('Created')}</th>
                    <th className="webhooks-th webhooks-thRight">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id} className={idx === rows.length - 1 ? 'webhooks-trLast' : ''}>
                      <td className="webhooks-td">{row.name}</td>
                      <td className="webhooks-td" style={{ fontFamily: 'monospace' }}>{row.keyMasked}</td>
                      <td className="webhooks-td">{row.companyName}</td>
                      <td className="webhooks-td">
                        <span
                          className={[
                            'webhooks-statusPill',
                            String(row.status || 'active').toLowerCase(),
                            theme,
                          ].join(' ')}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="webhooks-td">{formatCreated(row.createdAt)}</td>
                      <td className="webhooks-td webhooks-actionsCell">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          aria-label={t('apiKeyEditTitle')}
                          title={t('apiKeyEditTitle')}
                          className={['webhooks-iconBtn', theme].join(' ')}
                        >
                          <span className="oi oi-ellipses" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="webhooks-muted webhooks-tableEmpty">
                {t('apiKeysEmpty')}
              </div>
            )}

            {total > 0 && (
              <div
                style={{
                  padding: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  borderTop: `1px solid ${currentTheme.border}`,
                  background: theme === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
                }}
              >
                <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>
                  {t('Items per page')}: {PAGE_SIZE}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={['webhooks-secondaryBtn', theme, safePage <= 1 ? 'disabled' : 'enabled'].join(' ')}
                  >
                    {t('Prev')}
                  </button>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary, whiteSpace: 'nowrap' }}>
                    {t('Page')} {safePage} {t('of')} {totalPages}
                  </div>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={[
                      'webhooks-secondaryBtn',
                      theme,
                      safePage >= totalPages ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('Next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!!editing && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', theme].join(' ')}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div className="webhooks-modal">
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">
                  {editMode === 'create' ? t('apiKeyCreateTitle') : t('apiKeyEditTitle')}
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
                <div className="webhooks-formGrid">
                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('company')}</div>
                    <select
                      className={['webhooks-select', theme].join(' ')}
                      value={form.company_id}
                      onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))}
                      disabled={editMode === 'edit'}
                    >
                      <option value="">{t('select')}</option>
                      {companyOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                      {editMode === 'edit' &&
                        form.company_id &&
                        !companyOptions.some((c) => c.id === form.company_id) && (
                          <option value={form.company_id}>
                            {editing?.companyName && editing.companyName !== '—'
                              ? editing.companyName
                              : form.company_id}
                          </option>
                        )}
                    </select>
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('apiKeyName')}</div>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">
                      {t('apiKeyValue')}
                      {keyCopied && (
                        <span style={{ marginLeft: 8, fontWeight: 500, color: currentTheme.textSecondary }}>
                          {t('apiKeyCopied')}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div className="webhooks-secretWrap" style={{ flex: 1, minWidth: 0 }}>
                        <input
                          className={['webhooks-input', 'webhooks-secretInput', theme].join(' ')}
                          type={showKey ? 'text' : 'password'}
                          value={keyLoading ? '' : form.key}
                          readOnly
                          onClick={onCopyKey}
                          placeholder={keyLoading ? t('loading') : t('apiKeyCopyHint')}
                          title={keyLoading ? t('loading') : t('apiKeyCopyHint')}
                          style={{
                            cursor: form.key && !keyLoading ? 'pointer' : 'default',
                            opacity: keyLoading ? 0.7 : 1,
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowKey((v) => !v)}
                          className={['webhooks-iconBtn', 'webhooks-secretToggle', theme].join(' ')}
                          aria-label={showKey ? t('HideSecret') : t('ShowSecret')}
                          title={showKey ? t('HideSecret') : t('ShowSecret')}
                        >
                          <span className="oi oi-eye" aria-hidden="true" />
                        </button>
                      </div>
                      {editMode === 'create' && (
                        <button
                          type="button"
                          onClick={onGenerateKey}
                          className={['webhooks-iconBtn', theme].join(' ')}
                          aria-label={t('apiKeyGenerate')}
                          title={t('apiKeyGenerate')}
                        >
                          <span className="oi oi-key" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                    {editMode === 'create' && generatedKeyWarning && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
                        {t('apiKeyGeneratedOneTimeWarning')}
                      </div>
                    )}
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('Status')}</div>
                    <select
                      className={['webhooks-select', theme].join(' ')}
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      <option value="active">{t('apiKeyStatusActive')}</option>
                      <option value="revoked">{t('apiKeyStatusRevoked')}</option>
                      <option value="inactive">{t('apiKeyStatusInactive')}</option>
                      <option value="disabled">{t('apiKeyStatusDisabled')}</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="webhooks-modalFooter">
                {editMode === 'edit' && (
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={saving}
                    className={['webhooks-dangerBtn', theme, saving ? 'disabled' : 'enabled'].join(' ')}
                  >
                    {t('Delete')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className={['webhooks-secondaryBtn', theme, saving ? 'disabled' : 'enabled'].join(' ')}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving || !canSaveForm}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    saving || !canSaveForm ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {saving ? t('Saving') : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
